import { ICaptionsItem, PlayerStates } from '../../../types/common';
import { IPlayerManagerListener } from './playerManagerListener';
import { IPlayerStateChangeListener, YtUtils } from '../yt-utils';
import { PlayerController } from './playerController';
import { PlayerProvider } from '../provider/playerProvider';

export class PlayerManager implements IPlayerStateChangeListener {

  private static instance: PlayerManager;

  public static getInstance(): PlayerManager {
    if (!PlayerManager.instance)
      PlayerManager.instance = new PlayerManager();
    return PlayerManager.instance;
  }

  private targetCaptions!: ICaptionsItem[];
  private nativeCaptions!: ICaptionsItem[];
  private targetIndex!: number;
  private targetActiveWordIndex: number = 1;
  private nativeIndex!: number;
  private timer!: number;
  private listeners: IPlayerManagerListener[] = [];
  private processTimer?: ReturnType<typeof setInterval> | null;
  private playLastStartTime: number = 0;
  private pauseLastStartTime: number = 0;
  private lockStateChange = false;

  constructor() {
    YtUtils.stateChangeListeners.push(this);
    this.startProcess();
  }

  private stopProcess() {
    this.timer = 0;
    /*if (this.processTimer) { do not stop timer for update active caption index on pause move player pos
      clearInterval(this.processTimer);
      this.processTimer = null;
    }*/
  }

  private startProcess() {
    if (!this.processTimer)
      this.processTimer = setInterval(this.checkProcess.bind(this), 50);
  }

  public addListener(listener: IPlayerManagerListener) {
    this.listeners.push(listener);
  }

  public removeListener(listener: IPlayerManagerListener) {
    const index = this.listeners.findIndex(l => l === listener);
    if (index >= 0) {
      this.listeners.splice(index, 1);
    }
  }

  public setTargetIndex(targetIndex: number) {
    this.targetIndex = targetIndex;
  }

  public setTargetCaptions(targetCaptions: ICaptionsItem[]) {
    this.targetCaptions = targetCaptions;
  }
  public getTargetCaptions(): ICaptionsItem[] {
    return this.targetCaptions;
  }

  public setNativeCaptions(nativeCaptions: ICaptionsItem[]) {
    this.nativeCaptions = nativeCaptions;
  }

  public setTimer(time: number) {
    this.timer = time;
  }

  public setLockStateChange(value: boolean) {
    this.lockStateChange = value;
  }

  public async startPlay(time?: number, text?: string) {
    this.stopProcess();

    this.playLastStartTime = new Date().getTime();
    if (time !== undefined) {
      await this.waitStartPlay(time);
    } else {
      const time = await PlayerProvider.getProvider().getCurrentTime();
      await PlayerProvider.getProvider().play(time, text);
    }

    this.startProcess();
  }

  public isCanPlayFromTime(): boolean {
    return PlayerProvider.getProvider().isCanPlayFromTime()
  }

  private async waitStartPlay(targetTime: number/*, playerApi: IPlayerApiProps*/): Promise<null> {
    const firstTime = await PlayerProvider.getProvider().getCurrentTime()
    const moveForward = targetTime >= firstTime;
    await PlayerProvider.getProvider().play(targetTime);

    return new Promise(resolve => {
      const timer = setInterval(async () => {
        const currentTime = await PlayerProvider.getProvider().getCurrentTime()
        if (moveForward) {
          if (currentTime >= targetTime) {
            clearInterval(timer);
            resolve(null);
          }
        } else {
          if (currentTime < firstTime) {
            clearInterval(timer);
            resolve(null);
          }
        }

      }, 50)
    })

  }

  public async onNativePlayClicked() {
    this.stopProcess();
    /*if (this.processTimer) {
      clearInterval(this.processTimer);
      this.processTimer = null;
    }*/
    this.playLastStartTime = new Date().getTime();
    this.startProcess();
  }

  public async pausePlay() {
    await PlayerProvider.getProvider().pause();
    this.pauseLastStartTime = new Date().getTime();
    this.stopProcess();
  }

  public async waitToStop() {
    const provider = PlayerProvider.getProvider();
    return new Promise(resolve => {
      const tm = setInterval(() => {
        if (provider.isPaused()) {
          clearInterval(tm);
          return resolve(0);
        }
      }, 100)
    })
  }

  public focusNotify(isPlay: boolean) {
    this.listeners.forEach(l => {
      if (l.onFocusNotify) {
        l.onFocusNotify(isPlay);
      }
    });
  }

  public async onNativePauseClicked() {
    this.pauseLastStartTime = new Date().getTime();
  }

  public async getActiveTargetCaptionIndex(): Promise<number> {
    const currentTime = await PlayerProvider.getProvider().getCurrentTime();
    const {index} = this.getActiveCaptionIndex(currentTime, this.targetCaptions);
    return index;
  }

  public async getCurrentTime(): Promise<number> {
    return await PlayerProvider.getProvider().getCurrentTime();
  }

  private async checkProcess() {
    const provider = PlayerProvider.getProvider()
    if (!provider.isCanPlayFromTime()) {
      if (provider.isFinished()) {
        this.timer = 0;
        this.listeners.forEach(l => {
          if (l.onTimerDone) {
            l.onTimerDone();
          }
        });
      }
      return;
    }

    const currentTime = await provider.getCurrentTime();
    if (this.timer) {
      if (currentTime >= this.timer) {
        this.timer = 0;
        this.listeners.forEach(l => {
          if (l.onTimerDone) {
            l.onTimerDone();
          }
        });
      }
    }
    if (this.targetCaptions) {
      const {index: targetIndex, wordIndex} = this.getActiveCaptionIndex(currentTime, this.targetCaptions);
      if (this.targetIndex !== targetIndex) {
        this.targetIndex = targetIndex;
        this.listeners.forEach(l => {
          if (l.onTargetIndexChange) {
            l.onTargetIndexChange(this.targetIndex);
          }
        });
      }
      if (wordIndex !== this.targetActiveWordIndex) {
        this.targetActiveWordIndex = wordIndex;
        this.listeners.forEach(l => {
          if (l.onTargetWordIndexChange) {
            l.onTargetWordIndexChange(this.targetActiveWordIndex);
          }
        });
      }
    }

    if (this.nativeCaptions) {
      const {index: nativeIndex}  = this.getActiveCaptionIndex(currentTime, this.nativeCaptions);
      if (this.nativeIndex !== nativeIndex) {
        this.nativeIndex = nativeIndex;
        this.listeners.forEach(l => {
          if (l.onNativeIndexChange) {
            l.onNativeIndexChange(this.nativeIndex)
          }
        });
      }
    }
  }

  private getActiveCaptionIndex(currentTime: number,
                                captions: ICaptionsItem[]): {index: number, wordIndex: number} {
    const len = captions?.length;
    if (!len) return {index: -1, wordIndex: -1};

    for(let i=0; i<len; i++) {
      const startTime = captions[i].startTime;
      let endTime = captions[i].endTime;
      if (i < captions.length - 1) {
        if (endTime > captions[i+1].startTime) {
          endTime = captions[i+1].startTime;
        }
      }
      if (currentTime >= startTime && currentTime < endTime) {
        let wordIndex = -1;
        if (captions[i].words?.length) {
          for (let wi=0; wi<captions[i].words!.length; wi++) {
            const startTime = captions[i].words![wi].time;
            if (startTime < currentTime) {
              wordIndex = wi;
            }
          }
        }
        return {index: i, wordIndex};
      }
    }
    return {index: -1, wordIndex: -1};
  }

  public onStateChange(playerState: PlayerStates) {
    const maxPeriod = 1500;
    if (playerState === PlayerStates.PLAYING) {
        PlayerController.getInstance().onStarPlay();
        const period = new Date().getTime() - this.playLastStartTime;
        if (period > maxPeriod) {
          PlayerController.getInstance().onNativePlayClicked();
        }
    } else if (playerState === PlayerStates.PAUSED) {
      const period = new Date().getTime() - this.pauseLastStartTime;
      if (period > maxPeriod) {
        PlayerController.getInstance().onNativePauseClicked();
      }
    }

  }
}
